/**
 * SanPlumb — Generación de Bill of Materials (BOM)
 *
 * Funciones puras que convierten un SystemData en filas de metrado listas
 * para renderizar en el panel BOM y exportar a XLSX.
 *
 * Origen de migración: legacy `sanplumb-mvp__1_.html` líneas 1568-1602
 * (bloque `const bom = React.useMemo(...)`).
 *
 * Cambios respecto al MVP:
 *   - Lógica extraída del componente React a módulo puro testeable
 *   - Tipos explícitos en BomRow (antes objeto suelto)
 *   - Factor de desperdicio explicitado como constante (antes literal 1.15)
 *   - Función `exportRowsToXLSXArray` para alimentar SheetJS sin acoplar
 */

import type { SystemData, Pipe, Accessory, Sanitary } from '../types';
import type { BrandState } from '../types/ui';
import { allPipes, allAccessories } from '../types';

// ──────────────────────────────────────────────────────────────────
// Constantes
// ──────────────────────────────────────────────────────────────────

/**
 * Factor de desperdicio aplicado a la longitud total de tubería en el BOM.
 * Cubre cortes, recortes, sobrantes inutilizables y errores de medida.
 * 1.15 = 15% extra sobre la longitud teórica.
 */
export const FACTOR_DESPERDICIO_TUBERIA = 1.15;

// ──────────────────────────────────────────────────────────────────
// Tipos
// ──────────────────────────────────────────────────────────────────

/** Categoría de fila del BOM — usada para agrupar y aplicar colores. */
export type BomCategoria = 'tubería' | 'accesorio' | 'válvula' | 'reducción' | 'sanitario';

export interface BomRow {
  /** Color hex del punto indicador (var(--af), var(--ac), etc.). */
  dot: string;
  /** Descripción legible en el panel y XLSX. */
  label: string;
  /** Cantidad formateada (ej. "12.5 ml", "× 3 und", "× 1 und"). */
  qty: string;
  /** Categoría para agrupar y exportar. */
  cat: BomCategoria;
  /** Sistema: 'AF' | 'AC' | undefined (cuando no aplica, como sanitarios). */
  sys?: 'AF' | 'AC';
  /** Material si aplica (PVC-P, CPVC). */
  mat?: 'PVC-P' | 'CPVC';
}

// ──────────────────────────────────────────────────────────────────
// Colores (referencias a CSS vars para coherencia con §V)
// ──────────────────────────────────────────────────────────────────

const COLOR_AF = '#00C2FF';
const COLOR_AC = '#FF4040';
const COLOR_ACC_AF = '#F4A261';
const COLOR_ACC_AC = '#FF8C42';
const COLOR_VALVE = '#FFB800';
const COLOR_REDUCTION = '#8B5CF6';
const COLOR_SANITARY = '#1a4a80';

// ──────────────────────────────────────────────────────────────────
// Generadores parciales
// ──────────────────────────────────────────────────────────────────

/**
 * Agrupa tuberías por sistema y diámetro, sumando longitudes.
 * Aplica el factor de desperdicio al total.
 *
 * Exportada para tests; el caller normal usa `buildBOM()`.
 */
export function buildPipeRows(pipes: readonly Pipe[]): BomRow[] {
  const rows: BomRow[] = [];

  for (const sys of ['AF', 'AC'] as const) {
    const color = sys === 'AF' ? COLOR_AF : COLOR_AC;
    const mat = sys === 'AF' ? 'PVC-P' : 'CPVC';
    const labelSys = sys === 'AF' ? 'Agua Fría' : 'Agua Caliente (AC)';

    const pipesBySys = pipes.filter((p) => p.system === sys);

    // Agrupar por diámetro
    const lenByDiam = new Map<string, number>();
    for (const p of pipesBySys) {
      const prev = lenByDiam.get(p.diam) ?? 0;
      lenByDiam.set(p.diam, prev + (p.len ?? 0));
    }

    for (const [diam, len] of lenByDiam) {
      const totalConDesperdicio = len * FACTOR_DESPERDICIO_TUBERIA;
      rows.push({
        dot: color,
        label: `Tubería ${mat} ${diam} — ${labelSys}`,
        qty: `${totalConDesperdicio.toFixed(1)} ml`,
        cat: 'tubería',
        sys,
        mat,
      });
    }
  }

  return rows;
}

/**
 * Quita del spec el contexto entre paréntesis y separadores secundarios
 * (— : · seguidos de texto) para agrupar accesorios equivalentes en el
 * BOM. Conserva el TIPO + DIÁMETRO + MATERIAL.
 *
 * Ejemplos:
 *   "Codo galvanizado ½" (punto salida Lavatorio AF)"  → "Codo galvanizado ½""
 *   "Codo PVC ½" 90° (piso → muro ducha AF)"           → "Codo PVC ½" 90°"
 *   "Tee mezcladora ½" (ducha — AF + AC → regadera)"   → "Tee mezcladora ½""
 *
 * Las filas del ensamblaje de la llave de paso (lpPartsRows) NO pasan por
 * aquí — su spec ya está normalizado en `(ensamblaje llave de paso ${sys})`
 * y se debe respetar.
 */
function normalizeSpec(raw: string): string {
  // Quitar texto entre paréntesis (incluyendo paréntesis)
  let s = raw.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  // Quitar separadores secundarios + texto descriptivo después
  s = s.replace(/\s*(?:—|–|:|·)\s.*$/, '').trim();
  // Colapsar espacios múltiples
  s = s.replace(/\s+/g, ' ');
  const lower = s.toLowerCase();
  const material = lower.includes('cpvc') ? 'CPVC' : lower.includes('pvc') ? 'PVC-P' : '';
  const diam =
    lower.includes('3/4') || lower.includes('¾')
      ? '¾"'
      : lower.includes('1/2') || lower.includes('½')
        ? '½"'
        : '';

  if (lower.includes('codo galvanizado')) return `Codo galvanizado ${diam}`.trim();
  if (lower.includes('codo') && lower.includes('90')) {
    return `Codo 90° ${material} ${diam}`.replace(/\s+/g, ' ').trim();
  }
  if (lower.includes('tee mezcladora')) return `Tee mezcladora ${diam}`.trim();
  if (lower.includes('tee') && material) return `Tee ${material} ${diam}`.trim();
  if (lower.includes('reducciÃ³n') || lower.includes('reducción') || lower.includes('bushing')) {
    return `Reducción ${material} ¾"→½"`.replace(/\s+/g, ' ').trim();
  }
  if (lower.includes('uniÃ³n universal') || lower.includes('unión universal')) {
    return `Unión universal ${material} ${diam}`.replace(/\s+/g, ' ').trim();
  }
  if (lower.includes('niple')) return `Niple ${material} ${diam}`.replace(/\s+/g, ' ').trim();
  if (lower.includes('vÃ¡lvula') || lower.includes('válvula')) return `Válvula ${diam}`.trim();

  return s;
}

/**
 * Componentes individuales del ensamblaje de la llave de paso §VII —
 * BOM precision (v1.17). Antes la llave de paso se contabilizaba como
 * UNA línea; ahora se desagrega en sus 13 piezas para que el metrado sea
 * exacto en obra.
 *
 * Composición §VII (mkLlavePaso):
 *   - 4 codos 90° ½" (esquinas del ensamblaje)
 *   - 2 uniones universales ½"
 *   - 1 válvula (compuerta o esférica según catálogo)
 *   - 6 niples ½" (los tramos cortos del U)
 */
function lpPartsRows(sys: 'AF' | 'AC'): { spec: string; count: number; cat: BomCategoria }[] {
  const mat = sys === 'AF' ? 'PVC-P' : 'CPVC';
  const valveType = sys === 'AC' ? 'esférica' : 'compuerta';
  return [
    {
      spec: `Codo 90° ${mat} ½" (ensamblaje llave de paso ${sys})`,
      count: 4,
      cat: 'accesorio',
    },
    {
      spec: `Unión universal ${mat} ½" (ensamblaje llave de paso ${sys})`,
      count: 2,
      cat: 'accesorio',
    },
    {
      spec: `Niple ${mat} ½" (ensamblaje llave de paso ${sys})`,
      count: 6,
      cat: 'accesorio',
    },
    {
      spec: `Válvula ${valveType} ½" (cuerpo de llave de paso ${sys})`,
      count: 1,
      cat: 'válvula',
    },
  ];
}

/**
 * Agrupa accesorios por especificación+sistema y cuenta unidades.
 *
 * Los accesorios `isSwitchboard: true` (llaves de paso §VII) se DESAGRUPAN
 * en sus componentes (4 codos + 2 UU + 6 niples + 1 válvula) para que el
 * metrado tenga la precisión que exige la obra. Decisión sellada v1.17 —
 * antes era una sola línea.
 *
 * Reglas de categorización del resto:
 *  - Si el spec contiene "válvula" → 'válvula'
 *  - Si el spec contiene "reducción" o "bushing" → 'reducción'
 *  - Caso contrario → 'accesorio'
 */
export function buildAccessoryRows(accessories: readonly Accessory[]): BomRow[] {
  // Map: key (spec + sys) → { row data, count, cat opcional sobreescrito }
  const grouped = new Map<
    string,
    { spec: string; sys: 'AF' | 'AC'; count: number; cat?: BomCategoria }
  >();

  function add(spec: string, sys: 'AF' | 'AC', n: number, cat?: BomCategoria): void {
    const normalized = normalizeSpec(spec);
    const key = `${sys}|${normalized}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.count += n;
      if (!existing.cat && cat) existing.cat = cat;
    } else {
      grouped.set(key, { spec: normalized, sys, count: n, cat });
    }
  }

  for (const acc of accessories) {
    if (acc.isSwitchboard) {
      // Desagregar la llave de paso en sus piezas constituyentes
      for (const part of lpPartsRows(acc.system)) {
        add(part.spec, acc.system, part.count, part.cat);
      }
      continue;
    }
    // Normalizar el spec para agrupación: quitar el contexto entre
    // paréntesis (ej. "(punto salida Lavatorio AF)") y de "—"/":" en
    // adelante. Así "Codo galvanizado ½" (punto salida Lavatorio AF)" y
    // "Codo galvanizado ½" (punto salida Ducha AF)" se cuentan juntos
    // como una sola línea — el BOM lista cantidades por accesorio, no
    // por aparato (v1.21).
    const raw = acc.spec || acc.type;
    add(raw, acc.system, 1);
  }

  const rows: BomRow[] = [];
  for (const { spec, sys, count, cat: catOverride } of grouped.values()) {
    const specLower = spec.toLowerCase();
    const isValve = specLower.includes('válvula');
    const isReduction = specLower.includes('reducción') || specLower.includes('bushing');

    const cat: BomCategoria =
      catOverride ?? (isValve ? 'válvula' : isReduction ? 'reducción' : 'accesorio');
    const dot =
      cat === 'válvula'
        ? COLOR_VALVE
        : cat === 'reducción'
          ? COLOR_REDUCTION
          : sys === 'AC'
            ? COLOR_ACC_AC
            : COLOR_ACC_AF;

    rows.push({
      dot,
      label: spec,
      qty: `× ${count} und`,
      cat,
      sys,
    });
  }

  return rows;
}

/** Genera una fila por sanitario (siempre cantidad 1). */
export function buildSanitaryRows(sanitaries: readonly Sanitary[]): BomRow[] {
  return sanitaries.map((s) => ({
    dot: COLOR_SANITARY,
    label: `${s.type} (${s.subtype || '—'})${s.has_ac ? ' · AF+AC' : ' · solo AF'}`,
    qty: '× 1 und',
    cat: 'sanitario' as const,
  }));
}

// ──────────────────────────────────────────────────────────────────
// API pública
// ──────────────────────────────────────────────────────────────────

/**
 * Genera el BOM completo de un SystemData.
 * Orden de filas: tuberías AF, tuberías AC, accesorios, sanitarios.
 */
export function buildBOM(data: SystemData): BomRow[] {
  return [
    ...buildPipeRows(allPipes(data)),
    ...buildAccessoryRows(allAccessories(data)),
    ...buildSanitaryRows(data.sanitaries),
  ];
}

// ──────────────────────────────────────────────────────────────────
// Export helpers (acoplamiento mínimo con SheetJS)
// ──────────────────────────────────────────────────────────────────

/** Fila de la hoja XLSX principal (array of arrays formato SheetJS). */
export type XLSXRow = readonly (string | number)[];

/**
 * Convierte filas BOM en arrays listos para SheetJS `utils.aoa_to_sheet`.
 * Devuelve cabecera + filas.
 */
export function bomToXLSXRows(rows: readonly BomRow[], brands?: BrandState): XLSXRow[] {
  const header: XLSXRow = ['Descripción', 'Sistema', 'Material', 'Categoría', 'Cantidad', 'Marca ref.'];
  const body: XLSXRow[] = rows.map((r) => [
    r.label,
    r.sys ?? '—',
    r.mat ?? '—',
    r.cat,
    r.qty,
    brandForBomRow(r, brands),
  ]);
  return [header, ...body];
}

function brandForBomRow(row: BomRow, brands?: BrandState): string {
  if (!brands) return row.sys === 'AC' ? 'Vinilit' : row.sys === 'AF' ? 'PAVCO' : 'TREBOL';
  if (row.cat === 'sanitario') return brands.fixtures;
  if (row.cat === 'válvula') return row.sys === 'AC' ? brands.valveAC : brands.valveAF;
  if (row.sys === 'AC') return brands.pipeAC;
  if (row.sys === 'AF') return brands.pipeAF;
  return `${brands.pipeAF}/${brands.pipeAC}`;
}

/**
 * Genera la hoja "Resumen" del XLSX con totales por sistema.
 * Suma longitudes SIN aplicar factor de desperdicio (longitud nominal).
 */
export function buildResumenXLSXRows(data: SystemData): XLSXRow[] {
  const totalAF = data.pipes.reduce((s, p) => s + (p.len ?? 0), 0);
  const totalAC = data.pipes_ac.reduce((s, p) => s + (p.len ?? 0), 0);

  return [
    ['RESUMEN', ''],
    ['Sistema', 'Total tubería (longitud nominal)'],
    ['Agua Fría (AF) — PVC-P', `${totalAF.toFixed(1)} ml`],
    ['Agua Caliente (AC) — CPVC', `${totalAC.toFixed(1)} ml`],
    ['', ''],
    ['Notas', ''],
    [`Factor de desperdicio aplicado al BOM principal:`, `${FACTOR_DESPERDICIO_TUBERIA} (+${((FACTOR_DESPERDICIO_TUBERIA - 1) * 100).toFixed(0)}%)`],
  ];
}
